import { useEffect, useState } from 'react';
import { brandAPI } from '../lib/api';

// Brand identity (wordmark + logo) driven by the CMS `settings` doc, fetched
// once and shared across the app. Cached in localStorage for instant paint so
// the shell never flashes a placeholder on reload.
const FALLBACK = { acronym: '', name: '', wordmark: 'MMS', logo: '', logo_light: '', palette: {} };

let cache = null;
let inflight = null;

function readLS() {
  try {
    return JSON.parse(localStorage.getItem('mms_brand')) || null;
  } catch {
    return null;
  }
}

// The whole MMS UI is built on var(--mms-*) (Tailwind maps mms-* → these vars),
// so writing the CMS-managed palette onto :root re-themes the entire app.
// Missing keys are left untouched → the index.css defaults stay in force.
const MMS_KEYS = [
  'bg', 'surface', 'raised', 'border', 'text', 'muted', 'faint',
  'accent', 'accent_ink', 'danger', 'success', 'warning', 'info',
  'sidebar', 'topbar', 'input',
];
function applyPalette(palette) {
  if (!palette) return;
  const root = document.documentElement;
  MMS_KEYS.forEach((k) => {
    const v = palette[k];
    if (v) root.style.setProperty(`--mms-${k.replace(/_/g, '-')}`, v);
  });
}

// Instant paint on reload from the last cached palette, before React mounts.
applyPalette((readLS() || {}).palette);

export function useBrand() {
  const [brand, setBrand] = useState(cache || readLS() || FALLBACK);

  useEffect(() => {
    if (cache) {
      setBrand(cache);
      return undefined;
    }
    let alive = true;
    const p =
      inflight ||
      (inflight = brandAPI
        .get()
        .then(({ data }) => {
          cache = data;
          try {
            localStorage.setItem('mms_brand', JSON.stringify(data));
          } catch {
            /* ignore quota/privacy errors */
          }
          if (data.wordmark) document.title = data.wordmark;
          applyPalette(data.palette);
          return data;
        })
        .catch(() => cache || readLS() || FALLBACK)
        .finally(() => {
          inflight = null;
        }));
    p.then((d) => {
      if (alive) setBrand(d);
    });
    return () => {
      alive = false;
    };
  }, []);

  return brand;
}
